import { createClient } from '@sanity/client'
import { createImageUrlBuilder } from '@sanity/image-url'
import type { SanityImageSource } from '@sanity/image-url'

// Initialize Sanity client
export const client = createClient({
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID || '',
  dataset: import.meta.env.VITE_SANITY_DATASET || 'production',
  apiVersion: import.meta.env.VITE_SANITY_API_VERSION || '2024-01-01',
  useCdn: true, // Use CDN for faster response times
})

// Image URL builder for optimized images
const builder = createImageUrlBuilder(client)

export function urlFor(source: SanityImageSource) {
  return builder.image(source)
}

// Helper to get video URL from Sanity
export function getVideoUrl(ref: string): string {
  const [, id, extension] = ref.split('-')
  return `https://cdn.sanity.io/files/${client.config().projectId}/${client.config().dataset}/${id}.${extension}`
}

// Fetch all categories
export async function getCategories() {
  return await client.fetch(`
    *[_type == "category"] | order(order asc) {
      _id,
      name,
      slug,
      description,
      thumbnail,
      emblem,
      order,
      section->{
        name,
        slug
      }
    }
  `)
}

// Fetch projects by category
export async function getProjectsByCategory(categorySlug: string) {
  return await client.fetch(
    `
    *[_type == "project" && category->slug.current == $categorySlug] | order(publishedAt desc) {
      _id,
      title,
      slug,
      description,
      featuredVideo,
      videoThumbnail,
      category->{
        name,
        slug
      },
      publishedAt
    }
  `,
    { categorySlug }
  )
}

// Fetch single project by slug
export async function getProject(slug: string) {
  return await client.fetch(
    `
    *[_type == "project" && slug.current == $slug][0] {
      _id,
      title,
      slug,
      description,
      detailedDescription,
      featuredVideo,
      videoThumbnail,
      category->{
        name,
        slug,
        section->{
          name,
          slug
        }
      },
      section->{
        name,
        slug
      },
      publishedAt
    }
  `,
    { slug }
  )
}

// Fetch related projects
export async function getRelatedProjects(categoryId: string, currentProjectId: string, limit = 3) {
  return await client.fetch(
    `
    *[_type == "project" && category._ref == $categoryId && _id != $currentProjectId] | order(publishedAt desc) [0...$limit] {
      _id,
      title,
      slug,
      videoThumbnail,
      category->{
        name,
        slug
      }
    }
  `,
    { categoryId, currentProjectId, limit }
  )
}

// Fetch all sections
export async function getSections() {
  return await client.fetch(`
    *[_type == "section"] | order(order asc) {
      _id,
      name,
      slug,
      description,
      thumbnail,
      backgroundVideo,
      order,
      showCategoryFilter
    }
  `)
}

// Fetch single section by slug with its categories and direct project count
export async function getSectionWithCategories(sectionSlug: string) {
  return await client.fetch(
    `
    *[_type == "section" && slug.current == $sectionSlug][0] {
      _id,
      name,
      slug,
      description,
      thumbnail,
      backgroundVideo,
      order,
      showCategoryFilter,
      "categories": *[_type == "category" && section._ref == ^._id] | order(order asc) {
        _id,
        name,
        slug,
        description,
        thumbnail,
        emblem,
        order
      },
      "directProjectCount": count(*[_type == "project" && section._ref == ^._id])
    }
  `,
    { sectionSlug }
  )
}

// Fetch categories by section
export async function getCategoriesBySection(sectionSlug: string) {
  return await client.fetch(
    `
    *[_type == "category" && section->slug.current == $sectionSlug] | order(order asc) {
      _id,
      name,
      slug,
      description,
      thumbnail,
      emblem,
      order
    }
  `,
    { sectionSlug }
  )
}

// Fetch all projects by section (across all categories in that section OR with direct section reference)
export async function getProjectsBySection(sectionSlug: string) {
  return await client.fetch(
    `
    *[_type == "project" && (
      category->section->slug.current == $sectionSlug ||
      section->slug.current == $sectionSlug
    )] | order(publishedAt desc) {
      _id,
      title,
      slug,
      description,
      featuredVideo,
      videoThumbnail,
      category->{
        name,
        slug,
        section->{
          name,
          slug
        }
      },
      section->{
        name,
        slug
      },
      publishedAt
    }
  `,
    { sectionSlug }
  )
}
